/*
 * sanitiseHtml — DOMPurify wrapper used by the v-safe-html directive and
 * any component that needs to render user-supplied HTML without XSS risk.
 *
 * DOMPURIFY_CONFIG: frozen allowlist for Quill-generated content (headings,
 * lists, blockquotes, inline styles, safe links, images).
 *
 * sanitiseHtml(input): guard-checked wrapper that returns an empty string for
 * non-string input and delegates to DOMPurify.sanitize for everything else.
 */
import DOMPurify from 'dompurify'

let hookRegistered = false

function registerHookOnce () {
  if (hookRegistered) return
  hookRegistered = true
  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    // Force external links to open in a new tab safely.
    if (node.tagName === 'A' && node.hasAttribute('target')) {
      node.setAttribute('rel', 'noopener noreferrer')
      node.setAttribute('target', '_blank')
    }
  })
}

export const DOMPURIFY_CONFIG = Object.freeze({
  ALLOWED_TAGS: [
    'a', 'b', 'i', 'u', 's', 'strong', 'em', 'p', 'br', 'span', 'div',
    'blockquote', 'code', 'pre',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'sub', 'sup',
    'img'
  ],
  ALLOWED_ATTR: [
    'class', 'style', 'dir',
    'href', 'target', 'rel', 'title',
    'src', 'alt', 'width', 'height'
  ],
  // ADD_URI_SAFE_ATTR lists non-URI attributes whose values should NOT be
  // validated against ALLOWED_URI_REGEXP. Without this, DOMPurify strips
  // target="_blank" / rel="noopener noreferrer" / dir="ltr" / width / height
  // because those values do not match a URI scheme. Other attributes here
  // (class, style, alt, title) are already in DOMPurify's DEFAULT_URI_SAFE_ATTRIBUTES
  // — only the five below need an explicit override.
  ADD_URI_SAFE_ATTR: ['target', 'rel', 'dir', 'width', 'height'],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel|data:image\/(?:png|jpe?g|gif|webp);base64,)/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link', 'meta', 'base'],
  FORBID_ATTR: ['srcdoc', 'formaction', 'onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur']
})

export function sanitiseHtml (input) {
  if (input == null || typeof input !== 'string') {
    return ''
  }
  registerHookOnce()
  return DOMPurify.sanitize(input, DOMPURIFY_CONFIG)
}

export default sanitiseHtml
